import { defineStore } from 'pinia';
import { useCartCookie, useCreateCart, useCreateCartLine, useCart, useCartDiscountCodesUpdate } from '@/composables/shopify';
import { ref, computed } from 'vue';
import { mapProductToGa4Item } from '@/utils/ga4Map';
import { cartLineToProduct } from '@/utils/cartToProduct';

export const useCartStore = defineStore('cart', () => {
  const cart = ref(null);
  const loading = ref(false);
  const error = ref(null);
  
  // Initialize GTM proxy at store level (only on client side)
  let gtmProxy = null;
  if (process.client) {
    try {
      gtmProxy = useScriptGoogleTagManager()?.proxy;
    } catch (e) {
      console.warn('Failed to initialize GTM proxy in cart store:', e);
    }
  }

  const totalItems = computed(() => {
    return cart.value ? cart.value.totalQuantity : 0;
  });

  const totalPrice = computed(() => {
    return cart.value ? cart.value.cost.totalAmount.amount : 0;
  });

  const isEmpty = computed(() => {
    return totalItems.value === 0;
  });

  const discountCodes = computed(() =>
    cart.value?.discountCodes?.map((c) => ({
      code: c.code,
      applicable: c.applicable
    })) ?? []
  );

  async function initCart() {
    console.log('Init cart');
    try {
      loading.value = true;

      const cartCookie = useCartCookie();


      if (cartCookie && cartCookie.value) {
        console.log('Found cartId in cookie:', cartCookie.value);

        const fetchCart = useCart(cartCookie.value);
        const data = await fetchCart();

        if (data && data.cart) {
          cart.value = data.cart; // Återställ kundvagnen
          return;
        }
      }


      // Om ingen befintlig kundvagn finns, skapa en ny
      console.log('No cart found, creating a new one');

      await createNewCart(cartCookie);
    } catch (e) {
      console.error('Error in initCart:', e);
      error.value = e;
    } finally {
      loading.value = false;
    }
  }

  function updateCart(newCart) {
    cart.value = newCart;
  }

  async function createNewCart(cartCookie) {
    const { mutate } = useCreateCart();
    const { data } = await mutate({ lines: [] });

    if (data && data.cartCreate && data.cartCreate.cart) {
      cart.value = data.cartCreate.cart; // Spara kundvagnen i minnet
      try {
        if (cartCookie) {
          cartCookie.value = cart.value.id; // Uppdatera cookien
        }
      } catch (e) {
        console.log('Error setting cart cookie', e);
        //console.error('Error setting cart cookie:', e);
      }

      // Spara cartId i localStorage
      if (import.meta.env.SSR === false) {
        localStorage.setItem('cartId', cart.value.id);
      }
    } else {
      throw new Error('Failed to create a new cart');
    }
  }

  async function addToCart(variantId) {
    try {
      loading.value = true;
      const { mutate } = useCreateCartLine();
      const { data } = await mutate({
        cart: cart.value.id,
        lines: [
          {
            merchandiseId: variantId,
            quantity: 1,
          },
        ],
      });

      // Kolla av om vi överhuvudtaget har data eller userErrors
      if (!data || !data.cartLinesAdd) {
        throw new Error('Något gick fel: Saknar data.cartLinesAdd');
      }
      if (data.cartLinesAdd.userErrors && data.cartLinesAdd.userErrors.length) {
        console.error('UserErrors:', data.cartLinesAdd.userErrors);
        throw new Error('CartLinesAdd fel: ' + JSON.stringify(data.cartLinesAdd.userErrors));
      }

      // Om vi kommer hit är allt OK
      cart.value = data.cartLinesAdd.cart;

      // Hitta den tillagda cartLine
      const addedCartLine = data.cartLinesAdd.cart.lines.edges.find(
        (line) => line.node.merchandise.id === variantId
      );

      // Track add_to_cart event to GTM
      try {
        if (gtmProxy && addedCartLine && addedCartLine.node) {
          const cartLine = addedCartLine.node;
          
          // Build product object from cart line using utility function
          const product = cartLineToProduct(cartLine);

          const item = mapProductToGa4Item({
            product,
            listId: window.location.pathname,
            listName: window.location.pathname
          });

          if (item) {
            const currency = 
              cartLine.merchandise.price?.currencyCode ||
              cartLine.cost?.totalAmount?.currencyCode ||
              'EUR';

            gtmProxy.dataLayer.push({
              event: 'add_to_cart',
              ecommerce: {
                currency,
                items: [{ ...item, quantity: cartLine.quantity || 1 }]
              },
              pageUrl: window.location.href
            });
          }
        }
      } catch (trackingError) {
        console.warn('Error while pushing add_to_cart analytics', trackingError);
      }

      return {
        success: true,
        cartLine: addedCartLine ? addedCartLine.node : null,
      };
    } catch (e) {
      console.error('Error in addToCart:', e);
      error.value = e;
      return {
        success: false,
        error: e,
      };
    } finally {
      loading.value = false;
    }
  }

  // Applicera rabattkod
  async function applyDiscountCode(code) {
    if (!cart.value?.id) throw new Error('Ingen cart att lägga rabatt på');
    loading.value = true;
    error.value = null;
    try {
      const { mutate } = useCartDiscountCodesUpdate();
      const { data } = await mutate({
        cartId: cart.value.id,
        discountCodes: [code]
      });
      if (data?.cartDiscountCodesUpdate?.userErrors?.length) {
        throw new Error(data.cartDiscountCodesUpdate.userErrors.map((e) => e.message).join(', '));
      }
      cart.value = data.cartDiscountCodesUpdate.cart;
      return {
        success: true,
        cart: cart.value,
      }
    } catch (e) {
      error.value = e;
      return {
        success: false,
        error: e,
      }
    } finally {
      loading.value = false;
    }
  }

  // Ta bort rabattkod(er)
  async function removeDiscountCode() {
    if (!cart.value?.id) throw new Error('Ingen cart att ta bort rabatt från');
    loading.value = true;
    error.value = null;
    try {
      const { mutate } = useCartDiscountCodesUpdate();
      const { data } = await mutate({
        cartId: cart.value.id,
        discountCodes: []
      });
      if (data?.cartDiscountCodesUpdate?.userErrors?.length) {
        throw new Error(data.cartDiscountCodesUpdate.userErrors.map((e) => e.message).join(', '));
      }
      cart.value = data.cartDiscountCodesUpdate.cart;
      return {
        success: true,
        cart: cart.value,
      }
    } catch (e) {
      error.value = e;
      return {
        success: false,
        error: e,
      }
    } finally {
      loading.value = false;
    }
  }

  return {
    cart,
    loading,
    error,
    totalItems,
    totalPrice,
    isEmpty,
    discountCodes,
    initCart,
    addToCart,
    updateCart,
    applyDiscountCode,
    removeDiscountCode
  };
});